import type { DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import { L_SHAPE, RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import { GROOVING_TYPE_CODE_WITHOUT_GROOVING } from "../../../http/deckDesigner/types/groovingTypes";
import type {
  DeckReportData,
  ReportCodeQuantity,
  ReportJoistSupports,
  ReportLabel,
  ReportProduct,
} from "../../../http/deckDesigner/types/report";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { BillRowKind } from "./billOfQuantities.types";
import { type ProductBillRow, toOrder } from "./billOfQuantitiesRows";
import { computeNormalizedPosition, FIT_BOUNDS_PADDING_RATIO, getEffectiveFitBounds } from "./CartesianPlane";
import {
  computeSupportReactions,
  getBottomViewMeasurementExamples,
  getBottomViewMeasurementLabels,
  getSupportNodeReferencePoints,
  getSupportSpacingInfo,
  type SupportNodeType,
  type SupportReaction,
} from "./deckBottomViewGeometry";
import { getEdgeMeasurementLabels } from "./DeckEdgeMeasurements";
import { getCoordinateBounds, isPointInPolygon } from "./deckGeometry";
import { getSlopeDirectionDegrees, isSlopedTerrain } from "./deckOutline";
import { kilonewtonsPerSquareMeterToPsf, metersToFeet } from "./imperialConversions";
import type { MeasurementFormat } from "./measurementFormat";
import { isFiniteNumber } from "../../../utils/numberFormat";
import type { ReportValues } from "./reportValues";
import { calculateValidSupportRange } from "./supportHeight";
import type { useDeckBottomViewGeometry } from "./useDeckBottomViewGeometry";

// Meters - small relative to any real deck dimension, just enough to sample a point clearly on one
// side of an edge without landing back on the boundary itself.
const LABEL_PUSH_TEST_EPSILON = 0.01;
const NOT_AVAILABLE_VALUE = "/";

const SUBSTRUCTURE_PRODUCT_GROUP_KINDS: BillRowKind[] = ["supports", "support_screw", "substructure"];
const CONNECTOR_PRODUCT_GROUP_KINDS: BillRowKind[] = [
  "connector",
  "connector_substructure_screw",
  "connector_board_screw",
  "secondary_item",
];

// Everything but the images, which are captured only when the PDF is downloaded, and what the caller
// adds (language, date, names).
export type DeckReportDataWithoutImages = Omit<
  DeckReportData,
  | "language"
  | "date"
  | "configurationName"
  | "projectName"
  | "topView"
  | "sideView"
  | "bottomView"
  | "slopeView"
  | "accessoryImages"
  | "supportReactions"
> & { supportReactions: Omit<NonNullable<DeckReportData["supportReactions"]>, "supportFamilyImage" | "image"> | null };

export type DeckReportPayload = {
  report: DeckReportDataWithoutImages;
  topViewLabels: ReportLabel[];
  bottomViewLabels: ReportLabel[];
  supportReactionLabels: ReportLabel[];
};

export type DeckReportPayloadInput = {
  result: DeckDesignerResult;
  rows: ProductBillRow[];
  // The form values the report shows that aren't part of the result.
  values: Pick<
    DeckDesignerConfigurationFormValues,
    | "groovingType"
    | "groovingHeight"
    | "longDifferentBoardWidth"
    | "shortDifferentBoardWidth"
    | "slopeDegrees"
    | "slopeArrow"
  >;
  geometry: Pick<
    ReturnType<typeof useDeckBottomViewGeometry>,
    "bottomLayout" | "coordinates" | "isStripeSupport" | "selectedShape" | "supportFamilyCode"
  >;
  measurementFormat: MeasurementFormat;
  reportValues: ReportValues;
};

// The PDF report payload: values only, the server writes every label (see DeckReportData), plus the
// drawing labels and support reactions. Built only when the PDF is downloaded, not on every render
// like the bill rows.
export const buildDeckReportPayload = ({
  result,
  rows,
  values: { groovingType, groovingHeight, longDifferentBoardWidth, shortDifferentBoardWidth, slopeDegrees, slopeArrow },
  geometry: { bottomLayout, coordinates, isStripeSupport, selectedShape, supportFamilyCode },
  measurementFormat,
  reportValues,
}: DeckReportPayloadInput): DeckReportPayload => {
  const { isImperial, numberValue, piecesValue, lengthValue, mmValue, liveLoadValue, reactionValue, orEmpty } =
    reportValues;
  const isSloped = isSlopedTerrain({ slopeDegrees, slopeArrow });
  const isGroovingApplicable = groovingType?.code !== GROOVING_TYPE_CODE_WITHOUT_GROOVING;
  const substructureMaterialCode = result.substructureMaterialClass?.substructureMaterial.code;

  // Only meaningful once a specific support is picked - without one, min/max collapse to the same
  // fixed-stack value (see calculateValidSupportRange), which isn't a real range to report.
  const deckHeightRange = result.support
    ? calculateValidSupportRange(
        result.substructure,
        result.boardThickness,
        result.support,
        result.secondaryItem,
        result.connector,
        null,
      )
    : null;
  // cm with 1 decimal, not 0 - rounding to whole centimetres would lose up to ±5mm of the
  // precision calculateValidSupportRange actually computed.
  const minimumDeckHeight = lengthValue((deckHeightRange?.minimumDeckHeightMm ?? 0) / 10, "cm", 1);
  const maximumDeckHeight = lengthValue((deckHeightRange?.maximumDeckHeightMm ?? 0) / 10, "cm", 1);

  const supportSpacingInfo = getSupportSpacingInfo(
    bottomLayout.joists,
    result.supportQuantity,
    supportFamilyCode,
    isStripeSupport,
  );
  const joistSupports = (
    quantity: number | undefined,
    spacingMeters: number | null | undefined,
    supportsCount: number | null | undefined,
  ): ReportJoistSupports => ({
    quantity: result.substructureQuantity ? orEmpty(piecesValue(quantity)) : null,
    supportSpacing: lengthValue(spacingMeters, "m", 3),
    supportsCount: piecesValue(supportsCount),
  });

  // Both the top-view and bottom-view captures are fitted to this same outline (no per-view extra
  // margin), so a single normalized position per plane point is valid for either image. Widened the
  // same way CartesianPlane widens its own export-mode fit frame (see getEffectiveFitBounds) - an
  // extremely long, narrow deck would otherwise fit its raw bounds and land every label's fx/fy at
  // an image scale too thin to hold any text.
  const shapeBounds = getCoordinateBounds(coordinates);
  const effectiveShapeBounds = shapeBounds ? getEffectiveFitBounds(shapeBounds) : null;
  const maxCoordinates = selectedShape === RECTANGULAR_SHAPE ? 4 : 6;

  const topViewLabels: ReportLabel[] = effectiveShapeBounds
    ? getEdgeMeasurementLabels(coordinates, maxCoordinates).map((label) => {
        // Push the label away from the filled shape, into whichever side is actually empty - can't
        // just compare against the bounding-box center (LABEL_PUSH_TEST_EPSILON below), since that's
        // wrong for a label anchored on a concave edge, e.g. an L-shape's cutout side.
        const testPoint =
          label.orientation === "horizontal"
            ? { x: label.point.x, y: label.point.y - LABEL_PUSH_TEST_EPSILON }
            : { x: label.point.x + LABEL_PUSH_TEST_EPSILON, y: label.point.y };

        return {
          ...computeNormalizedPosition(label.point, effectiveShapeBounds, FIT_BOUNDS_PADDING_RATIO),
          orientation: label.orientation,
          rotate: label.rotate,
          value: isImperial
            ? { text: measurementFormat.length(label.lengthMeters, "m", "") }
            : { number: label.lengthMeters, minDecimals: 2, decimals: 3, unit: "m" },
          sideCode: label.sideCode,
          pushPositive: !isPointInPolygon(testPoint, coordinates),
        };
      })
    : [];

  const bottomViewMeasurementExamples = getBottomViewMeasurementExamples(
    bottomLayout.joists,
    result.supportQuantity,
    supportFamilyCode,
    isStripeSupport,
  );
  const bottomViewLabels: ReportLabel[] =
    shapeBounds && effectiveShapeBounds
      ? getBottomViewMeasurementLabels<Pick<ReportLabel, "value" | "supportsCount">>(
          bottomViewMeasurementExamples,
          shapeBounds,
          (spacingMeters) => ({ value: lengthValue(spacingMeters, "m", 3) }),
          (supportCount) => ({ value: piecesValue(supportCount), supportsCount: true }),
        ).map((label) => ({
          ...computeNormalizedPosition(label.point, effectiveShapeBounds, FIT_BOUNDS_PADDING_RATIO),
          orientation: label.orientation,
          rotate: label.rotate,
          ...label.text,
        }))
      : [];

  // --- Support reactions: one row per node type present, plus one reference label per type on the
  // bottom-view drawing (all of them would be unreadable). Point supports only - none for stripe
  // GRANULO, and nothing without a support or a live load.
  const supportReactions =
    result.support && isFiniteNumber(result.liveLoad)
      ? computeSupportReactions(
          bottomLayout.joists,
          result.supportQuantity,
          supportFamilyCode,
          isStripeSupport,
          result.liveLoad,
        )
      : [];
  const reactionsByNodeType = new Map<SupportNodeType, { count: number; maxReaction: SupportReaction }>();
  supportReactions.forEach((reaction) => {
    const current = reactionsByNodeType.get(reaction.nodeType);
    reactionsByNodeType.set(reaction.nodeType, {
      count: (current?.count ?? 0) + 1,
      maxReaction: current && current.maxReaction.reactionKn >= reaction.reactionKn ? current.maxReaction : reaction,
    });
  });
  const nodeTypes = [...reactionsByNodeType.keys()].sort((a, b) => a - b);
  // The formulas follow the unit of measure: qk in psf times lengths in feet gives the reaction in
  // lbf, as kN/m² times meters gives kN.
  const toFormulaLength = (meters: number) => (isImperial ? metersToFeet(meters, 3) : meters);
  const maxSupportReactionKn = supportReactions.reduce((max, reaction) => Math.max(max, reaction.reactionKn), 0);
  const supportReactionLabels: ReportLabel[] = effectiveShapeBounds
    ? [...getSupportNodeReferencePoints(supportReactions).entries()]
        .sort(([a], [b]) => a - b)
        .map(([nodeType, point]) => ({
          ...computeNormalizedPosition(point, effectiveShapeBounds, FIT_BOUNDS_PADDING_RATIO),
          orientation: "horizontal" as const,
          value: { text: String(nodeType) },
          onAnchor: true,
        }))
    : [];

  const toProduct = (row: ProductBillRow): ReportProduct => ({
    kind: row.kind,
    id: row.productId,
    code: row.code,
    quantity: row.quantityValue,
  });
  const codeQuantity = (code: string | undefined, quantity: number | null | undefined): ReportCodeQuantity | null =>
    code && isFiniteNumber(quantity) ? { code, quantity: piecesValue(quantity) } : null;

  const report: DeckReportDataWithoutImages = {
    imperial: isImperial,
    shape: result.shape ? (result.shape === RECTANGULAR_SHAPE ? "rectangular" : "lShaped") : null,
    references: {
      fasteningTypeCode: result.fasteningType?.code ?? null,
      groovingTypeCode: groovingType?.code ?? null,
      connectorFamilyCode: result.connector?.connectorFamily.code ?? null,
      substructureMaterialCode: substructureMaterialCode ?? null,
      substructureMaterialClassId: result.substructureMaterialClass?.id ?? null,
      substructureId: result.substructure?.id ?? null,
      supportFamilyCode: result.support?.supportFamily.code ?? null,
    },

    liveLoad: isFiniteNumber(result.liveLoad) ? liveLoadValue(result.liveLoad) : null,
    deflection: isFiniteNumber(result.deflection) ? result.deflection : null,
    maxBase: lengthValue(result.base, "m", 2),
    maxHeight: lengthValue(result.height, "m", 2),
    secondaryBase: result.shape !== RECTANGULAR_SHAPE ? lengthValue(result.secondaryBase, "m", 2) : null,
    secondaryHeight: result.shape !== RECTANGULAR_SHAPE ? lengthValue(result.secondaryHeight, "m", 2) : null,
    totalHeight: lengthValue(result.totalHeight, "cm"),
    // Flat terrain until both are set, same as what's saved (see isSlopedTerrain).
    slope: isSloped ? numberValue(slopeDegrees, "°", 3) : numberValue(0, "°"),
    slopeUphillDirection: isSloped ? numberValue(getSlopeDirectionDegrees(slopeArrow), "°", 1) : null,

    horizontalBoards: result.hasHorizontalBoards ?? null,
    groovingThickness:
      isGroovingApplicable && isFiniteNumber(result.connector?.connectorFamily.groovingThickness)
        ? mmValue(result.connector.connectorFamily.groovingThickness)
        : { text: NOT_AVAILABLE_VALUE },
    groovingHeight:
      isGroovingApplicable && isFiniteNumber(groovingHeight) ? mmValue(groovingHeight) : { text: NOT_AVAILABLE_VALUE },
    gap: isFiniteNumber(result.gap) ? mmValue(result.gap) : null,
    boardThickness: isFiniteNumber(result.boardThickness) ? mmValue(result.boardThickness) : null,
    boardWidth: isFiniteNumber(result.boardWidth) ? mmValue(result.boardWidth) : null,
    shortBoards:
      result.boardQuantity && selectedShape === L_SHAPE
        ? orEmpty(piecesValue(result.boardQuantity.regularShort))
        : null,
    longBoards:
      result.boardQuantity && selectedShape === L_SHAPE ? orEmpty(piecesValue(result.boardQuantity.regularLong)) : null,
    totalBoards:
      result.boardQuantity && selectedShape !== L_SHAPE ? orEmpty(piecesValue(result.boardQuantity.regularLong)) : null,
    shortDifferentBoard: result.boardQuantity?.differentShort
      ? {
          width: lengthValue(shortDifferentBoardWidth, "mm"),
          quantity: piecesValue(result.boardQuantity.differentShort),
        }
      : null,
    longDifferentBoard: result.boardQuantity?.differentLong
      ? {
          width: lengthValue(longDifferentBoardWidth, "mm"),
          quantity: piecesValue(result.boardQuantity.differentLong),
        }
      : null,

    substructureSection: result.substructure
      ? {
          base: result.substructure.base == null ? null : mmValue(result.substructure.base),
          height: mmValue(result.substructure.height),
        }
      : null,
    longJoist:
      supportSpacingInfo.long != null || result.supportQuantity?.long != null
        ? joistSupports(result.substructureQuantity?.long, supportSpacingInfo.long, result.supportQuantity?.long)
        : null,
    shortJoist:
      selectedShape === L_SHAPE && (supportSpacingInfo.short != null || result.supportQuantity?.short != null)
        ? joistSupports(result.substructureQuantity?.short, supportSpacingInfo.short, result.supportQuantity?.short)
        : null,
    supportReactions: nodeTypes.length
      ? {
          liveLoad: isFiniteNumber(result.liveLoad) ? liveLoadValue(result.liveLoad) : null,
          maxReaction: reactionValue(maxSupportReactionKn),
          formulaLiveLoad: isFiniteNumber(result.liveLoad)
            ? isImperial
              ? numberValue(kilonewtonsPerSquareMeterToPsf(result.liveLoad), undefined, 1)
              : numberValue(result.liveLoad)
            : null,
          nodes: nodeTypes.map((nodeType) => {
            const { count, maxReaction } = reactionsByNodeType.get(nodeType)!;
            return {
              nodeType,
              count,
              reaction: reactionValue(maxReaction.reactionKn),
              terms: maxReaction.terms.map((term) => ({
                joistSpacing: toFormulaLength(term.joistSpacing),
                lengthAlongJoist: toFormulaLength(term.lengthAlongJoist),
              })),
            };
          }),
        }
      : null,

    deckArea: isFiniteNumber(result.deckArea)
      ? isImperial
        ? { text: measurementFormat.area(result.deckArea, "") }
        : numberValue(result.deckArea, "m²", 2)
      : null,
    deckHeightRange: deckHeightRange
      ? {
          min: minimumDeckHeight,
          max: deckHeightRange.minimumDeckHeightMm === deckHeightRange.maximumDeckHeightMm ? null : maximumDeckHeight,
        }
      : null,
    maxSupportSpacing: result.supportQuantity ? orEmpty(lengthValue(result.supportQuantity.maxSpacingCm, "cm")) : null,
    supports: piecesValue(toOrder(result.quantities.supports, result.support?.item)),
    joists: result.substructureQuantity ? orEmpty(piecesValue(result.substructureQuantity.total)) : null,
    longJoists: result.substructureQuantity ? orEmpty(piecesValue(result.substructureQuantity.long)) : null,
    shortJoists: result.substructureQuantity ? orEmpty(piecesValue(result.substructureQuantity.short)) : null,
    connectors: piecesValue(toOrder(result.quantities.connectors, result.connector?.item)),
    connectorSubstructureScrew: codeQuantity(
      result.connectorSubstructureScrew?.item.code,
      toOrder(result.quantities.connectorSubstructureScrews, result.connectorSubstructureScrew?.item),
    ),
    supportScrew: codeQuantity(
      result.supportScrew?.item.code,
      toOrder(result.quantities.supportScrews, result.supportScrew?.item),
    ),

    substructureProducts: rows.filter((row) => SUBSTRUCTURE_PRODUCT_GROUP_KINDS.includes(row.kind)).map(toProduct),
    connectorProducts: rows.filter((row) => CONNECTOR_PRODUCT_GROUP_KINDS.includes(row.kind)).map(toProduct),
  };

  return { report, topViewLabels, bottomViewLabels, supportReactionLabels };
};
